import { Component, Input, OnInit, OnDestroy, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, inject, ViewChild, ElementRef } from '@angular/core';

import WaveSurfer from 'wavesurfer.js';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-audio-player',
  standalone: true,
  imports: [ButtonModule, TooltipModule],
  templateUrl: './audio-player.html',
  styleUrl: './audio-player.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AudioPlayer implements OnInit, AfterViewInit, OnDestroy {
  @Input() audioUrl!: string;
  @Input() compact: boolean = false;

  @ViewChild('waveformContainer', { static: false }) waveformContainer!: ElementRef;

  private cdr = inject(ChangeDetectorRef);
  private wavesurfer: WaveSurfer | null = null;

  containerId: string = '';

  isPlaying = false;
  isLoading = true;
  hasError = false;
  currentTime = 0;
  duration = 0;

  ngOnInit(): void {
    this.containerId = 'waveform-' + Math.random().toString(36).substr(2, 9);
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.initWaveSurfer(), 100);
  }

  ngOnDestroy(): void {
    if (this.wavesurfer) {
      this.wavesurfer.destroy();
    }
  }

  private initWaveSurfer(): void {
    if (!this.audioUrl) return;

    // Check if container exists
    const container = document.getElementById(this.containerId);
    if (!container) {
      console.error('WaveSurfer container not found:', this.containerId);
      this.isLoading = false;
      this.hasError = true;
      this.cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this.hasError = false;

    if (this.wavesurfer) {
      this.wavesurfer.destroy();
      this.wavesurfer = null;
    }

    this.wavesurfer = WaveSurfer.create({
      container: `#${this.containerId}`,
      waveColor: 'rgb(200, 200, 200)',
      progressColor: 'rgb(100, 100, 200)',
      height: this.compact ? 40 : 60,
      barWidth: 2,
      barGap: 1,
      barRadius: 2
    });

    this.wavesurfer.load(this.audioUrl);

    this.wavesurfer.on('ready', () => {
      this.duration = this.wavesurfer?.getDuration() || 0;
      this.isLoading = false;
      this.hasError = false;
      this.cdr.markForCheck();
    });

    this.wavesurfer.on('error', (error) => {
      console.error('WaveSurfer error:', error);
      this.isLoading = false;
      this.hasError = true;
      this.cdr.markForCheck();
    });

    this.wavesurfer.on('play', () => {
      this.isPlaying = true;
      this.cdr.markForCheck();
    });

    this.wavesurfer.on('pause', () => {
      this.isPlaying = false;
      this.cdr.markForCheck();
    });

    this.wavesurfer.on('timeupdate', (currentTime) => {
      this.currentTime = currentTime;
      this.cdr.markForCheck();
    });
  }

  togglePlay(): void {
    if (this.wavesurfer) {
      this.wavesurfer.playPause();
    }
  }

  skipBackward(): void {
    if (this.wavesurfer) {
      const newTime = Math.max(0, this.wavesurfer.getCurrentTime() - 5);
      this.wavesurfer.setTime(newTime);
    }
  }

  skipForward(): void {
    if (this.wavesurfer) {
      const newTime = Math.min(this.duration, this.wavesurfer.getCurrentTime() + 5);
      this.wavesurfer.setTime(newTime);
    }
  }

  /**
   * Перематывает на указанную секунду и продолжает играть.
   *
   * Нужен карточке разговора: клик по реплике должен ставить звук на эту
   * реплику, а не просто прокручивать список.
   */
  seekTo(seconds: number, autoplay: boolean = true): void {
    if (!this.wavesurfer) {
      return;
    }

    this.wavesurfer.setTime(Math.max(0, Math.min(seconds, this.duration || seconds)));

    if (autoplay && !this.isPlaying) {
      this.wavesurfer.play();
    }
  }

  downloadAudio(): void {
    if (this.audioUrl) {
      const link = document.createElement('a');
      link.href = this.audioUrl;
      link.download = 'audio.mp3';
      link.click();
    }
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}
